import { subDays } from "date-fns/esm";
import { memo, useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router";

import usePolledLazyQuery from "apollo/usePolledQuery/usePolledLazyQuery";
import Filters from "components/Filters";
import FiltersContentWrapper from "components/Filters/ContentWrapper";
import FiltersPanel from "components/Filters/FiltersPanel";
import { useCachedFilterFields } from "components/Filters/hooks";
import FiltersSidebar from "components/Filters/Sidebar";
import SortDropdown from "components/Filters/SortDropdown";
import FiltersSplit from "components/Filters/Split";
import { SavedFilterView, SortOption } from "components/Filters/types";
import FlashContext from "components/FlashMessages/FlashContext";
import { getSearchQuery } from "components/SearchInput/helpers";
import Box from "ds/components/Box";
import DatepickerRange from "ds/components/Datepicker/Range";
import Toggle from "ds/components/Toggle";
import { AnalyticsPageNotifications } from "hooks/useAnalytics/pages/notifications";
import useTypedContext from "hooks/useTypedContext";
import { SearchQueryPredicate, SearchSuggestionsOutput } from "types/generated";
import { PollingIntervalGroups } from "apollo/constants";
import useMakeFiltersData from "components/Filters/useMakeFiltersData";

import {
  FILTERS_ORDER_SETTINGS_KEY,
  INITIAL_PERIOD,
  initialSortDirection,
  initialSortOption,
} from "../constants";
import { NotificationsContext } from "../Context";
import DismissAllNotificationsButton from "../DismissAllNotificationsButton";
import { SEARCH_NOTIFICATIONS_SUGGESTIONS } from "../gql";
import { FILTERS_EXCLUDED_FIELDS, notificationsSuggestionsDictionary, PERIODS } from "./constants";

type StackFiltersLayoutProps = {
  allSelected: boolean;
  onSelectAll: () => void;
  onResetAll: () => void;
  predicates: SearchQueryPredicate[];
  children: React.ReactNode;
  hasItems: boolean;
  currentSavedView?: SavedFilterView;
  setCurrentSavedView: (view?: SavedFilterView) => unknown;
  newNotificationsCount?: number;
};

const NotificationsFiltersLayout = ({
  allSelected,
  onSelectAll,
  onResetAll,
  predicates,
  children,
  hasItems,
  currentSavedView,
  setCurrentSavedView,
  newNotificationsCount,
}: StackFiltersLayoutProps) => {
  const fieldsRef = useRef<string[] | null>(null);

  const { setCurrentDateRange, currentDateRange } = useTypedContext(NotificationsContext);

  const [searchParams] = useSearchParams();
  const searchInput = getSearchQuery(searchParams);

  const { onError } = useTypedContext(FlashContext);

  const [loadSearchNotificationsSuggestions, { data: filteringData, loading, refetch }] =
    usePolledLazyQuery<{
      searchNotificationsSuggestions: SearchSuggestionsOutput;
    }>(SEARCH_NOTIFICATIONS_SUGGESTIONS, { pollingGroup: PollingIntervalGroups.Lists });

  const cachedFiltersData = useCachedFilterFields(
    filteringData?.searchNotificationsSuggestions?.fields
  );

  const sortOptions = useMemo((): SortOption[] => {
    return (
      cachedFiltersData
        .filter((field) => field.orderable)
        .map((field) => {
          return {
            value: field.name,
            label: notificationsSuggestionsDictionary[field.name] || field.name,
          };
        }) || []
    );
  }, [cachedFiltersData]);

  const { filters, filterOptionsMap } = useMakeFiltersData({
    suggestions: cachedFiltersData,
    dictionary: notificationsSuggestionsDictionary,
    excludedFilterFields: FILTERS_EXCLUDED_FIELDS,
  });

  const handleSelectAllToggle = () => {
    if (!allSelected) {
      onSelectAll();
    } else {
      onResetAll();
    }
  };

  // Unmount/navigation mid-request aborts the pending query - not a real failure,
  // so don't surface it to the user.
  const reportIfNotAborted = (error: Error) => {
    if (error?.name !== "AbortError") {
      onError(error);
    }
  };

  const loadSearchSuggestions = (initial = false) => {
    loadSearchNotificationsSuggestions({
      variables: {
        input: {
          fullTextSearch: searchInput,
          predicates,
          fields: initial ? null : fieldsRef.current,
        },
      },
    }).catch(reportIfNotAborted);
  };

  const handlePollingActiveSections = (fields: string[]) => {
    fieldsRef.current = fields;

    refetch({
      input: {
        fullTextSearch: searchInput,
        predicates,
        fields,
      },
    }).catch(reportIfNotAborted);
  };

  useEffect(() => {
    loadSearchSuggestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [predicates, searchInput]);

  // initial request with nullish fields
  useEffect(() => {
    loadSearchSuggestions(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Filters
      filters={filters}
      filtersItemsOptionsMap={filterOptionsMap}
      filtersLoading={loading}
      sortOptions={sortOptions}
      initialSortOption={initialSortOption}
      initialSortDirection={initialSortDirection}
      pollActiveSections={handlePollingActiveSections}
      filtersOrderSettingsKey={FILTERS_ORDER_SETTINGS_KEY}
      filtersDictionary={notificationsSuggestionsDictionary}
      filtersType="notifications"
      currentSavedView={currentSavedView}
      setCurrentSavedView={setCurrentSavedView}
    >
      <FiltersSplit>
        <FiltersSidebar analyticsPage={AnalyticsPageNotifications.NotificationsList} />

        <FiltersContentWrapper>
          <FiltersPanel>
            <Box direction="row">
              {hasItems && (
                <Toggle
                  variant="checkbox"
                  id="select_all"
                  isSelected={allSelected}
                  onChange={handleSelectAllToggle}
                  aria-label={allSelected ? "Unselect all" : "Select all"}
                >
                  Select all
                </Toggle>
              )}
            </Box>

            <Box direction="row" gap="medium">
              {!!newNotificationsCount && <DismissAllNotificationsButton />}
              <DatepickerRange
                isButton
                periods={PERIODS}
                initialPeriod={INITIAL_PERIOD}
                endDate={currentDateRange.end}
                startDate={currentDateRange.start}
                onChange={setCurrentDateRange}
                minDate={subDays(new Date(), 14)}
                maxDate={new Date()}
              />

              <SortDropdown />
            </Box>
          </FiltersPanel>

          {children}
        </FiltersContentWrapper>
      </FiltersSplit>
    </Filters>
  );
};

export default memo(NotificationsFiltersLayout);
